import { useQuery, gql } from "@apollo/client";
import LoadingButton from '@mui/lab/LoadingButton';
import { format, parseISO } from 'date-fns';
import { useParams } from "react-router";
import { useRef, useState, useEffect } from "react";
import { dateOnlyISO8601 } from "../../../tools";
import { Box, Button, Card } from '@mui/material';
import { usePersonStudentAssignment } from "../../../hooks/Staff";
import { GridCallbackDetails, GridColDef, GridRow, GridRowModel, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import { useManageModal, TablePaginated, FetchMoreCallbackArgs, SearchBar, SearchToolbarProps } from "ui";
import { StudentCustomField, PersonStudentsWithLocationQuery, PersonStudentsWithLocationQueryVariables } from "../../../__generated__/graphql";
import { PersonDataType } from "../../../types";
export const PERSON_STUDENT_WITH_LOCATION_QUERY = gql(`
 query personStudentsWithLocation ($personId:UUID!, $query:String, $first:Int, $after:String){
  person (id:$personId) {
    locations{
        totalCount
    }
    locationStudents (query:$query,status:ACTIVE, first:$first, after:$after) {
        pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
        edges{
            cursor
            node{
                id
                firstName
                lastName
                dob
                references
                location {
                    id
                    name
                }
                persons(ids:[$personId]){
                    edges{
                        cursor
                        node{
                            id
                        }     
                    }  
                }
            }
        }
      totalCount
    }
  }
}`);
type ActionOnCallback = () => void;
type RouteParams = {
    id?: string
}

const createCustomField = (customField: any): GridColDef => {
    return {
        field: customField.name ?? 'missing',
        headerName: customField.name ?? 'missing',
        flex: 1,
        renderCell: (params) => {
            const studentCustomField = params.row.studentCustomFields
                .find((x: StudentCustomField) => x.customField?.id === customField.id) as StudentCustomField;

            return studentCustomField
                ? <>{studentCustomField.value}</>
                : null;
        }
    }
}

const createLocationField = (): GridColDef => {
    return {
        field: "location",
        headerName: "Location",
        sortable: true,
        flex: 1,
        renderCell: (params) => {
            return <>{params.row.location.name}</>
        },
        // valueGetter: (params: { row: any }) => `${params.row.location.name}`
    }
}

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}

const mapEdgesToTable = (data: any) => {
    return {
        ...data.node,
        id: data.node.id,
        formattedDob: format(parseISO(dateOnlyISO8601(data.node.dob)), 'MM/dd/yyyy'),
        student: data.node
        // dob: format(parseISO(dateOnlyISO8601(data.dob)), 'MM/dd/yyyy')
    }
}

function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {
    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

interface StaffTableLinkContainerProps {
    children: React.ReactNode
}


interface StaffTableLinkTableToolbarProps extends SearchToolbarProps { }

interface StaffTableLinkProps {
    onBack: ActionOnCallback,
    person: PersonDataType
}

interface PesonTableLinkFooterProps {
    saving: boolean,
    disabled: boolean,
    onBack: ActionOnCallback
    onSave: ActionOnCallback
}
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: 'name',
        headerName: "Child's Name",
        flex: 1,
        sortable: true,
        renderCell: (params: { row: any }) => <>{params.row.firstName} {params.row.lastName}</>
    },
    {
        field: 'dob',
        flex: 1,
        sortable: true,
        renderCell: (params: { row: any }) => <>{params.row.formattedDob}</>,
        headerName: 'Date of Birth',
    },
]

const DEFAULT_PAGE_SIZE = 10
const StaffTableLink = ({
    onBack,
    person
}: StaffTableLinkProps) => {
    const routeParams = useParams<RouteParams>();
    const isPaginatingRef = useRef(false);
    const { openModal } = useManageModal();
    const { loading, data, fetchMore } = useQuery<PersonStudentsWithLocationQuery, PersonStudentsWithLocationQueryVariables>(PERSON_STUDENT_WITH_LOCATION_QUERY, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            personId: routeParams?.id,
            first: DEFAULT_PAGE_SIZE,
            query: ""
        }
    });

    const [tableState, setTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[],
        initialSelectionModel: GridRowSelectionModel,
        selectionModel: GridRowSelectionModel,
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        },
    }>({
        columns: [],
        rows: [],
        initialSelectionModel: { type: 'include', ids: new Set() },
        selectionModel: { type: 'include', ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
    });

    const [saving, setSaving] = useState<boolean>(false);

    const { save } = usePersonStudentAssignment(routeParams.id ?? '');

    useEffect(() => {
        if (data) {
            setTableState((prevState) => {
                const columns = [...TABLE_COLUMNS]
                const rows = data?.person?.locationStudents?.edges?.map(mapEdgesToTable) ?? [];
                if ((data?.person?.locations?.totalCount ?? 0) > 1) {
                    columns.push(createLocationField())
                }
                const selectedIds = data?.person?.locationStudents?.edges?.filter(edge =>
                    edge.node.persons?.edges?.some(
                        (x: any) => x.node.id === routeParams.id
                    )
                ).map(edge => edge.node.id) ?? []
                const selectionModel: GridRowSelectionModel = { type: 'include', ids: new Set(selectedIds) };

                const _ids = Array.from(selectionModel.ids);
                return {
                    ...prevState,
                    columns,
                    rows: rows,
                    selectionModel,
                    selections: {
                        ...prevState.selections,
                        initial: _ids,
                        pageAssign: _ids,
                        prevAssign: unique(prevState.selections.prevAssign.concat(Array.from(selectionModel.ids))),
                        allSelected: Array.from(selectionModel.ids).concat(prevState.selections.nextAssign)
                    }
                }
            });
        }
    }, [loading, data]);
    const handleSelectionModelChange = (values: GridRowSelectionModel) => {
        if (isPaginatingRef.current) return; // ignore page-triggered changes

        const selectedIds = Array.from(values.ids);

        setTableState(prev => {
            const currentPageIds = new Set(prev.rows.map(r => r.id));
            const allSelected = new Set(prev.selections.allSelected);
            const prevPageSelected = Array.from(prev.selectionModel.ids);
            const assign = selectedIds.filter(id => !prevPageSelected.includes(id));
            const unassign = prevPageSelected.filter(id => !selectedIds.includes(id));


            // User selected rows -> add to global selection
            selectedIds.forEach(id => allSelected.add(id));

            // User deselected rows -> remove from global selection
            currentPageIds.forEach(id => {
                if (!selectedIds.includes(id)) allSelected.delete(id);
            });

            // Page-level selection for DataGrid display
            const pageSelectionIds = Array.from(allSelected).filter(id => currentPageIds.has(id));

            return {
                ...prev,
                selectionModel: { type: 'include', ids: new Set(pageSelectionIds) },
                selections: {
                    ...prev.selections, allSelected: Array.from(allSelected),
                    nextAssign: unique(deltaSelections(assign, unassign, prev.selections.nextAssign)),
                    unassign: deltaSelections(unassign, assign, prev.selections.unassign),
                },


            };
        });
    };

    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        isPaginatingRef.current = true
        await fetchMore({
            variables: {
                personIds: routeParams.id,
                query: variables.query ?? "",
                after: variables.after ?? null,
                first: variables.first ?? DEFAULT_PAGE_SIZE
            }
        }).then((res) => {
            const rows = res?.data?.person?.locationStudents?.edges?.map(mapEdgesToTable) ?? []
            if (rows && rows.length > 0) {
                setTableState(prevState => ({
                    ...prevState,
                    rows
                }))
            }
        }).catch(err => {
            // console.log('the is an error: ', err)
        }).finally(() => {
            setTimeout(() => (isPaginatingRef.current = false), 200)
        })
    }
    const handleSave = async () => {

        // In theory, this save event shouldn't fire because the save button should be
        // disabled. However, for completeness sake, we exit the event early if 
        // there's nothing to save.
        if (tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)
            return;

        setSaving(true);

        // When not in development mode this debug code is removed (tree shaken).
        if (process.env.NODE_ENV === 'development') {
            console.group('handleSave');
            console.groupEnd();
        }

        await save(tableState.selections.nextAssign, tableState.selections.unassign);

        setTableState(prevState => ({
            ...prevState,
            selections: {
                ...prevState.selections,
                pageAssign: [],
                prevAssign: [],
                nextAssign: [],
                unassign: []
            }
        }))

        setSaving(false);

        openModal({
            type: 'SAVED_ASSIGNMENTS',
            onConfirm: undefined,
            cancelButtonLabel: 'Close'
        });
    }

    return (
        <>
            <TablePaginated
                loading={loading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <StaffTableLink.TableToolbar
                            title={!loading
                                ? `Link Child for ${person?.firstName} ${person?.lastName}`
                                : 'Loading...'
                            }
                            value={searchQuery}
                            onChange={onSearch}
                            onClickBack={onBack}
                            clearSearch={onSearchClear} />
                    )
                }}
                slots={{
                    row: (props: any) => {
                        const selected = tableState.selections.initial.includes(props.row.id);
                        const found = props.row.persons.edges?.find((x: any) => x.node.id === person?.id);
                        /*
                         * `props.selected` is not true when the row is disabled using `isRowSelectable` 🤦‍♂️
                         * so to achieve the desired result, we look at the intial selection modal i.e., 
                         * all identified person/student connections found at time of loeading the
                         * component
                         */
                        const disabled = props.row.references <= 1 && selected && found;
                        const handleDisabledClick = () => {
                            openModal({ type: 'NOT_ENOUGH_REFERENCES' })
                        };

                        return (
                            <div
                                style={{
                                    background: disabled ? '#F5F5F5' : 'initial',
                                    color: disabled ? '#777' : 'initial',
                                    cursor: disabled ? 'not-allowed' : 'default'
                                }}
                                onClick={disabled ? handleDisabledClick : undefined}>
                                <GridRow {...props} />
                            </div>
                        )
                    }
                }}
                isRowSelectable={(params) => {
                    const selected = tableState.selections.initial.includes(params.row.id);
                    const found = params.row.persons.edges?.find((x: any) => x.node.id === person?.id);
                    const disabled = !(params.row.references <= 1 && found && selected);
                    return disabled;
                }}
                pageInfo={data?.person?.locationStudents?.pageInfo}
                totalRows={data?.person?.locationStudents?.totalCount ?? 0}
                columns={tableState.columns}
                rows={tableState.rows}
                pageSize={DEFAULT_PAGE_SIZE}
                selectionProps={{
                    rowSelectionModel: { type: 'include', ids: new Set(tableState.selections.allSelected) },
                    onRowSelectionModelChange: handleSelectionModelChange
                }}
                disableBoxShadow={true}
                checkboxSelection={true}
                onFetchMore={handleFetchMore}
                sortable={true} />
            <StaffTableLink.Footer
                disabled={(tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)}
                saving={saving}
                onBack={onBack}
                onSave={handleSave} />
        </>
    )
}

StaffTableLink.TableToolbar = (props: StaffTableLinkTableToolbarProps) => {
    return (
        <Box component="div">
            <SearchBar {...props} />
        </Box>
    );
}


StaffTableLink.Footer = ({ disabled, saving, onSave, onBack }: PesonTableLinkFooterProps): React.ReactElement => {
    return (
        <Box
            component="div"
            sx={{ padding: "16px", display: "flex", justifyContent: "flex-end" }}
        >
            <Button
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    marginRight: "16px",
                    backgroundColor: "#C4C4C4",
                    "&:hover": {
                        color: "#FFF",
                        backgroundColor: "red.main",
                    },
                }}
                onClick={onBack}
            >
                Cancel
            </Button>
            <LoadingButton
                loading={saving}
                disabled={disabled}
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                        backgroundColor: "primary.light",
                    },
                }}
                onClick={onSave}
            >
                Save
            </LoadingButton>
        </Box>
    )
}

export default StaffTableLink;